/**
 * The swarm choreography engine — the pure core extracted from NeighborField
 * (2026-08-27). Given N drones and a target formation, produce smooth,
 * collision-avoiding, physically-plausible motion: every drone seeks its
 * assigned slot while pushing smoothly off any neighbor inside its safe
 * radius. Converging into a tight shape jams up and then clears.
 *
 * Contract:
 * - ZERO imports. No three.js, no React, no DOM, no clocks, no Math.random.
 *   Runs identically in the browser, Node, or a server process.
 * - DETERMINISTIC: randomness comes from the injected seed (mulberry32),
 *   time is accumulated from the dt you pass in. Same seed + same inputs +
 *   same step sequence = bit-identical trajectories.
 * - UNIT-AGNOSTIC: `safe` and `maxSpeed` are in whatever length unit the
 *   positions use. The studio converts its light-unit dials before calling
 *   (1 lu = 0.08 scene units there); a server would speak meters.
 * - The WAVE field (push / waves / ripples) only modulates per-drone safe
 *   radii — it never writes positions. The physics produces the motion, so
 *   the swarm adapts, and a null/silent drive is an exact no-op.
 *
 * The caller owns rendering, easing of coarse inputs (scale), and unit
 * conversion. stepSwarm mutates state in place; the typed arrays inside
 * state are stable for the life of the swarm, so a renderer can bind them
 * once as GPU attributes.
 *
 * KNOWN LIMIT (measured 2026-08-27): separation scans at most MAX_NEIGH=48
 * neighbors per drone per step — a real-time rendering compromise. Where the
 * scan is not exhausted the model holds a strong floor (min pair distance
 * 0.90x safe under a 40-drone total collapse; 0.71-0.72x in realistic
 * 216-300 drone crams), but a pathological pile-up (150+ drones sent to ONE
 * point) exhausts the cap on far neighbors and close pairs can go unexamined
 * (measured 0.08x safe). A server-side bake mode should raise or lift the
 * cap; it exists so 6000 drones stay real-time in a browser tab.
 */

export type SwarmParams = {
  safe: number; // min comfortable center-to-center distance (position units)
  maxSpeed: number; // position units / second
  rigidity: number; // seek-to-slot spring strength
  avoidance: number; // separation force strength
  damping: number; // velocity damping
};

/** Audio-style modulation drive. Any unit null = that layer off. */
export type WaveDrive = {
  level: number; // overall energy 0..1
  bass: number; // low band 0..1
  beat: number; // kick envelope (1 on a fresh kick, fast decay)
  push: { frac: number; amt: number } | null; // inflating subset
  waves: { amt: number } | null; // traveling plane waves (bass-driven)
  ripples: { amt: number } | null; // beat-spawned expanding shells
};

export type FlowDrive = { speed: number }; // laps per minute along the curve order

export type Ripple = { x: number; y: number; z: number; age: number };

export type SwarmState = {
  n: number;
  time: number; // accumulated sim time (drives the wave phases)
  pos: Float32Array; // current positions (n*3)
  vel: Float32Array;
  tgt: Float32Array; // assigned slots, source-cloud order preserved via idx
  col: Float32Array; // current colors (eased)
  tgtCol: Float32Array;
  seeds: Float32Array; // per-drone 0..1 (also useful as a render twinkle phase)
  quant: Float32Array; // rank-quantile of seed: quant[i] < f selects EXACTLY round(f*n)
  rad: Float32Array; // per-drone safe radius this step
  ripples: Ripple[];
  prevBeat: number;
  idx: Int32Array; // drone -> source point index (curve order, for Flow)
  ord: Float32Array; // the target cloud in its own (curve) order
  flowOff: number; // Flow phase in laps (0..1)
  cellHead: Int32Array;
  next: Int32Array;
  rng: () => number;
};

// spatial hash so separation is ~O(n) instead of O(n^2)
const TABLE = 1 << 14;
const MASK = TABLE - 1;
const MAX_NEIGH = 48;

function hashCell(cx: number, cy: number, cz: number) {
  return (((cx * 92837111) ^ (cy * 689287499) ^ (cz * 283923481)) & MASK) >>> 0;
}

// two fixed plane-wave directions for the wave field (normalized)
const W1 = [0.81, 0.49, 0.28] as const;
const W2 = [-0.25, 0.83, -0.46] as const;
const MAX_RIPPLES = 4;
const RIPPLE_LIFE = 1.8;
const RIPPLE_SPEED = 7;
const RIPPLE_W = 0.9;

/** Small fast seeded PRNG (mulberry32). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Untangle a morph: reorder the target cloud so each drone flies to a nearby
 * slot instead of crossing the whole swarm. Cheap heuristic — sort both sets
 * by azimuth (then height) and map rank-to-rank. Dramatically cleaner than a
 * random assignment, and O(n log n) instead of the O(n^2) optimal transport.
 */
export function assign(
  current: Float32Array,
  targetPos: Float32Array,
  targetCol: Float32Array,
  n: number
): { positions: Float32Array; colors: Float32Array; indices: Int32Array } {
  const cmp = (arr: Float32Array) => (a: number, b: number) => {
    const aa = Math.atan2(arr[a * 3 + 2], arr[a * 3]);
    const ab = Math.atan2(arr[b * 3 + 2], arr[b * 3]);
    if (aa !== ab) return aa - ab;
    return arr[a * 3 + 1] - arr[b * 3 + 1];
  };
  const curIdx = Array.from({ length: n }, (_, i) => i).sort(cmp(current));
  const tarIdx = Array.from({ length: n }, (_, i) => i).sort(cmp(targetPos));

  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  const indices = new Int32Array(n); // drone -> target point index (source order)
  for (let r = 0; r < n; r++) {
    const drone = curIdx[r];
    const tgt = tarIdx[r];
    indices[drone] = tgt;
    positions[drone * 3] = targetPos[tgt * 3];
    positions[drone * 3 + 1] = targetPos[tgt * 3 + 1];
    positions[drone * 3 + 2] = targetPos[tgt * 3 + 2];
    colors[drone * 3] = targetCol[tgt * 3];
    colors[drone * 3 + 1] = targetCol[tgt * 3 + 1];
    colors[drone * 3 + 2] = targetCol[tgt * 3 + 2];
  }
  return { positions, colors, indices };
}

/** Build a swarm resting at (and targeting) the given cloud. */
export function createSwarm(positions: Float32Array, colors: Float32Array, seed = 1): SwarmState {
  const n = positions.length / 3;
  const rng = mulberry32(seed);
  const seeds = new Float32Array(n);
  const idx = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    seeds[i] = rng();
    idx[i] = i;
  }
  // rank-quantile of each seed: `quant[i] < frac` selects EXACTLY
  // round(frac*n) drones (a raw seed threshold is only approximate — at
  // 1 drone requested you could get 0 or 3). Sets nest as frac grows.
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => seeds[a] - seeds[b]);
  const quant = new Float32Array(n);
  for (let r = 0; r < n; r++) quant[order[r]] = (r + 0.5) / n;
  return {
    n,
    time: 0,
    pos: positions.slice(),
    vel: new Float32Array(n * 3),
    tgt: positions.slice(),
    col: colors.slice(),
    tgtCol: colors.slice(),
    seeds,
    quant,
    rad: new Float32Array(n),
    ripples: [],
    prevBeat: 0,
    idx,
    ord: positions,
    cellHead: new Int32Array(TABLE),
    next: new Int32Array(n),
    flowOff: 0,
    rng,
  };
}

/**
 * Fly to a new formation: untangle the target cloud against current
 * positions, keep the curve-order map for Flow. Physics does the transit.
 */
export function retarget(state: SwarmState, positions: Float32Array, colors: Float32Array): void {
  const a = assign(state.pos, positions, colors, state.n);
  state.tgt.set(a.positions);
  state.tgtCol.set(a.colors);
  state.idx.set(a.indices);
  state.ord = positions;
}

/**
 * Advance the swarm by dt seconds. Mutates state in place.
 * `scale` multiplies the target space (the caller eases it); `wave`
 * modulates per-drone safe radii; `flow` slides every slot along the
 * ordered curve so the swarm streams around the figure.
 */
export function stepSwarm(
  state: SwarmState,
  dt: number,
  opts: { params: SwarmParams; scale?: number; wave?: WaveDrive | null; flow?: FlowDrive | null }
): void {
  const p = opts.params;
  const sc = opts.scale ?? 1;
  const w = opts.wave ?? null;
  const fl = opts.flow ?? null;
  const { n: N, pos, vel, tgt, col, tgtCol, cellHead, next, quant, rad, ripples, idx, ord } = state;

  state.time += dt;
  const time = state.time;

  // Flow: advance the phase — each drone's slot slides along the ordered
  // curve, so the whole swarm STREAMS around the figure (physics chases the
  // moving slots, so it flows like a ribbon of traffic, never teleports)
  if (fl) state.flowOff = (state.flowOff + (fl.speed / 60) * dt) % 1;
  const flowT = fl ? state.flowOff * N : 0;

  const safe = Math.max(p.safe, 1e-3);
  const maxSp = p.maxSpeed;
  const damp = Math.max(0, 1 - p.damping * dt);
  const maxSp2 = maxSp * maxSp;
  const colRate = Math.min(dt * 2.5, 1);

  // ---- wave field: per-drone safe-radius multipliers ----
  const pushOn = !!(w && w.push);
  const wavesOn = !!(w && w.waves);
  const ripplesOn = !!(w && w.ripples);
  const audioOn =
    (pushOn || wavesOn || ripplesOn) &&
    !!w &&
    (w.level > 0.004 || w.bass > 0.004 || w.beat > 0.004);

  // beat -> spawn an expanding ripple shell at a random drone (rising edge:
  // the driver sets beat to 1 on a kick and decays it fast, so a fresh kick
  // is "beat high now, was low last step")
  const beat = w ? w.beat : 0;
  const beatEdge = beat > 0.9 && state.prevBeat < 0.5;
  state.prevBeat = beat;
  if (audioOn && ripplesOn && beatEdge && N > 0) {
    const o = (state.rng() * N) | 0;
    if (ripples.length >= MAX_RIPPLES) ripples.shift();
    ripples.push({ x: pos[o * 3], y: pos[o * 3 + 1], z: pos[o * 3 + 2], age: 0 });
  }
  for (let r = ripples.length - 1; r >= 0; r--) {
    ripples[r].age += dt;
    if (ripples[r].age > RIPPLE_LIFE) ripples.splice(r, 1);
  }

  let maxRad = safe;
  if (audioOn && w) {
    const wAmp = w.waves ? w.bass * 0.9 * w.waves.amt : 0; // traveling undulation
    const pulse = w.push ? w.level * 2.2 * w.push.amt : 0; // the inflating subset
    const pushFrac = w.push ? w.push.frac : 0;
    const rippleGain = w.ripples ? 1.6 * w.ripples.amt : 0;
    const k1 = 1.5, k2 = 1.9;
    for (let i = 0; i < N; i++) {
      const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
      let mMul = 1;
      if (quant[i] < pushFrac) mMul += pulse;
      mMul += wAmp * Math.sin((x * W1[0] + y * W1[1] + z * W1[2]) * k1 - time * 4.2);
      mMul += wAmp * 0.8 * Math.sin((x * W2[0] + y * W2[1] + z * W2[2]) * k2 + time * 3.1);
      for (let r = 0; r < ripples.length; r++) {
        const rp = ripples[r];
        const d = Math.hypot(x - rp.x, y - rp.y, z - rp.z) - rp.age * RIPPLE_SPEED;
        const q = d / RIPPLE_W;
        mMul += rippleGain * Math.exp(-q * q) * (1 - rp.age / RIPPLE_LIFE);
      }
      if (mMul < 0.35) mMul = 0.35;
      else if (mMul > 4) mMul = 4;
      const ri = safe * mMul;
      rad[i] = ri;
      if (ri > maxRad) maxRad = ri;
    }
  } else {
    rad.fill(safe);
    ripples.length = 0;
  }

  // hash cells sized to the largest radius so no inflated pair is missed
  const inv = 1 / maxRad;
  cellHead.fill(-1);
  for (let i = 0; i < N; i++) {
    const h = hashCell(
      Math.floor(pos[i * 3] * inv),
      Math.floor(pos[i * 3 + 1] * inv),
      Math.floor(pos[i * 3 + 2] * inv)
    );
    next[i] = cellHead[h];
    cellHead[h] = i;
  }

  for (let i = 0; i < N; i++) {
    const ix = i * 3, iy = ix + 1, iz = ix + 2;
    const px = pos[ix], py = pos[iy], pz = pos[iz];
    const radI = rad[i];

    // seek toward assigned slot (scaled into the current swarm space);
    // under Flow the slot itself slides along the ordered curve
    let sx, sy, sz;
    if (fl) {
      const t = idx[i] + flowT;
      const a0 = t | 0;
      const f = t - a0;
      const a3 = (a0 % N) * 3;
      const b3 = ((a0 + 1) % N) * 3;
      sx = ord[a3] + (ord[b3] - ord[a3]) * f;
      sy = ord[a3 + 1] + (ord[b3 + 1] - ord[a3 + 1]) * f;
      sz = ord[a3 + 2] + (ord[b3 + 2] - ord[a3 + 2]) * f;
    } else {
      sx = tgt[ix]; sy = tgt[iy]; sz = tgt[iz];
    }
    let ax = (sx * sc - px) * p.rigidity;
    let ay = (sy * sc - py) * p.rigidity;
    let az = (sz * sc - pz) * p.rigidity;

    // separation from neighbors in the 27 surrounding cells; the pair
    // threshold is the mean of the two drones' current (modulated) radii
    const cx = Math.floor(px * inv), cy = Math.floor(py * inv), cz = Math.floor(pz * inv);
    let visited = 0;
    for (let dx = -1; dx <= 1 && visited < MAX_NEIGH; dx++)
      for (let dy = -1; dy <= 1 && visited < MAX_NEIGH; dy++)
        for (let dz = -1; dz <= 1 && visited < MAX_NEIGH; dz++) {
          let j = cellHead[hashCell(cx + dx, cy + dy, cz + dz)];
          while (j >= 0 && visited < MAX_NEIGH) {
            if (j !== i) {
              visited++;
              const rx = px - pos[j * 3], ry = py - pos[j * 3 + 1], rz = pz - pos[j * 3 + 2];
              const d2 = rx * rx + ry * ry + rz * rz;
              const thresh = (radI + rad[j]) * 0.5;
              if (d2 < thresh * thresh && d2 > 1e-9) {
                const dist = Math.sqrt(d2);
                const f = (((thresh - dist) / thresh) * p.avoidance) / dist;
                ax += rx * f; ay += ry * f; az += rz * f;
              }
            }
            j = next[j];
          }
        }

    // integrate (damped), clamp speed
    let vx = (vel[ix] + ax * dt) * damp;
    let vy = (vel[iy] + ay * dt) * damp;
    let vz = (vel[iz] + az * dt) * damp;
    const v2 = vx * vx + vy * vy + vz * vz;
    if (v2 > maxSp2) {
      const s = maxSp / Math.sqrt(v2);
      vx *= s; vy *= s; vz *= s;
    }
    vel[ix] = vx; vel[iy] = vy; vel[iz] = vz;
    pos[ix] = px + vx * dt; pos[iy] = py + vy * dt; pos[iz] = pz + vz * dt;

    // ease color toward the target color
    col[ix] += (tgtCol[ix] - col[ix]) * colRate;
    col[iy] += (tgtCol[iy] - col[iy]) * colRate;
    col[iz] += (tgtCol[iz] - col[iz]) * colRate;
  }
}
